import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../../hooks/useDashTheme";

/* Shared pieces for the new Work Summary.
   ADDITIVE: nothing here is imported by any existing screen.

   Wording contract: this UI never shows raw input counts. The server folds
   them into an activity LEVEL (0-100 + label); everything below renders that
   level, never a count. Categories are plain verbs, not tool names. */

export const CATS = {
  deep:     { label: "Building",         key: "deep" },
  review:   { label: "Reviewing",        key: "review" },
  comms:    { label: "Meetings & chat",  key: "comms" },
  docs:     { label: "Writing",          key: "docs" },
  browse:   { label: "Researching",      key: "browse" },
  distract: { label: "Off-task",         key: "distract" },
  idle:     { label: "Away",             key: "idle" },
};

/* ── Category palette ─────────────────────────────────────────────────────
   NOT taken from tokens.accents. That spread was measured and fails hard as a
   categorical palette: `#A29B4E` (the old "Researching" olive) and `#2F8F9D`
   both fall under the chroma floor — they literally render as gray, which is
   why the page read muted — and `#B4589E`↔`#2F8F9D` sit at ΔE 0.3 for deutan
   viewers, i.e. identical. tokens.accents stays untouched for the rest of the
   product; this page uses a palette selected against its own two surfaces.

   Each mode is SELECTED for its surface, not flipped from the other.

   Validator results (OKLab ΔE ×100, surfaces #FFFFFF / #252728):
     · Donut + proportion strip use only slots 1-3 (blue, orange, aqua).
       Those three PASS every check on the all-pairs list in BOTH modes —
       which is why anything past third place folds into a neutral "Other"
       rather than growing a fourth hue.
     · The ribbon and the ranked list carry all seven. Six chromatic hues
       cannot clear the all-pairs floors in any ordering — so identity there
       is never colour-alone: every block is directly labelled, the legend is
       always present, and the tooltip names the category. That is the
       documented relief, not an oversight.
     · `idle` and `distract` are STATES, not work types. `idle` takes a
       neutral (absence of work reads as absence of hue) and `distract` takes
       the reserved status red, so neither competes as a categorical series. */
const CAT_PALETTE = {
  light: {
    deep:     "#2a78d6",   // blue    — slot 1, donut-safe
    browse:   "#eb6834",   // orange  — slot 2, donut-safe
    comms:    "#1baf7a",   // aqua    — slot 3, donut-safe
    docs:     "#4a3aa7",   // violet
    review:   "#e87ba4",   // magenta
    distract: "#e34948",   // reserved status red
    idle:     "#A9ABAD",   // neutral
  },
  dark: {
    deep:     "#3987e5",
    browse:   "#d95926",
    comms:    "#199e70",
    docs:     "#9085e9",
    review:   "#d55181",
    distract: "#e66767",
    idle:     "#626364",
  },
};

/** The three hues cleared for charts where every pair is adjacent. */
export const DONUT_SAFE = ["deep", "browse", "comms"];

export function useCatColors() {
  const t = useDashTheme();
  return React.useMemo(() => (t.isDark ? CAT_PALETTE.dark : CAT_PALETTE.light), [t.isDark]);
}

export const fmtDur = (m) => {
  const n = Math.max(0, Math.round(m || 0));
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60), r = n % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};

function fmtClockBrowser(ms) {
  const d = new Date(ms);
  const h24 = d.getHours();
  const ampm = h24 < 12 ? "AM" : "PM";
  const h = h24 % 12 || 12;
  return `${h}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
}

/* Set by Work Summary for the open day. fmtClock then paints org-local time
   even when a component forgot to pass offsetMin. UTC/0 from a stale /day
   payload is rejected upstream so this is never left at 0 for an IST org. */
let clockOffsetMin = null;
export function setWorkSummaryClockOffset(min) {
  clockOffsetMin = Number.isFinite(Number(min)) ? Number(min) : null;
}

export const fmtClockTz = (ms, offsetMin) => {
  const off = Number.isFinite(offsetMin) ? offsetMin : clockOffsetMin;
  if (!Number.isFinite(off)) return fmtClockBrowser(ms);
  const d = new Date(ms + off * 60000);
  const h24 = d.getUTCHours();
  const ampm = h24 < 12 ? "AM" : "PM";
  const h = h24 % 12 || 12;
  return `${h}:${String(d.getUTCMinutes()).padStart(2, "0")} ${ampm}`;
};

export const fmtClock = (ms) => fmtClockTz(ms, clockOffsetMin);

/** 0-23 → "8 AM" / "12 PM" — the hour-grid row label. */
export const hourLabel = (h) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;

export const activityTone = (t, v) =>
  v >= 85 ? t.c.ai : v >= 62 ? t.status.positive : v >= 35 ? t.c.primary : t.c.textTertiary;

/* ── KPI tile ─────────────────────────────────────────────────────────────
   Bullet-chart shape: the value is always readable as text, the track shows
   it against a target marker. Never colour alone. */
export function Kpi({ label, value, unit, delta, dir = "flat", foot, fill = 0, target, color, index = 0, chart }) {
  const t = useDashTheme();
  const [w, setW] = React.useState(0);
  React.useEffect(() => {
    const id = setTimeout(() => setW(Math.max(0, Math.min(100, fill))), 120 + index * 60);
    return () => clearTimeout(id);
  }, [fill, index]);

  const tone = dir === "up" ? t.status.positive : dir === "down" ? t.status.negative : t.c.textSecondary;
  const toneBg = dir === "up" ? t.status.positiveSoft : dir === "down" ? t.status.negativeSoft : t.status.neutralSoft;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: t.motion.ease.out, delay: index * 0.05 }}
      style={{
        background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
        padding: t.s[4], boxShadow: t.shadow.sm, position: "relative", overflow: "hidden",
      }}
    >
      {/* A tinted wash keyed to the tile's own metric. Without it four white
          tiles on a near-white canvas read as one flat band. */}
      <span aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `linear-gradient(135deg, ${color || t.c.primary}${t.isDark ? "14" : "0E"} 0%, transparent 62%)`,
      }} />
      <div style={{ position: "relative", fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, textTransform: "uppercase", letterSpacing: "0.07em", color: t.c.textTertiary, display: "flex", alignItems: "center", gap: 6 }}>
        <i style={{ width: 6, height: 6, borderRadius: 2, background: color || t.c.primary, flex: "none" }} />
        {label}
      </div>
      <div style={{ position: "relative", fontSize: 26, fontWeight: t.font.weight.semibold, letterSpacing: "-0.03em", margin: "8px 0 2px", display: "flex", alignItems: "baseline", gap: 6, fontVariantNumeric: "tabular-nums" }}>
        {value}
        {unit ? <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.medium, color: t.c.textSecondary }}>{unit}</span> : null}
      </div>
      <div style={{ position: "relative", fontSize: t.font.size.sm, color: t.c.textSecondary, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {delta != null && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontWeight: t.font.weight.semibold, fontSize: t.font.size.xs, padding: "2px 6px", borderRadius: t.r.pill, color: tone, background: toneBg }}>
            {dir === "up" ? "▲" : dir === "down" ? "▼" : "■"} {delta}
          </span>
        )}
        {foot ? <span>{foot}</span> : null}
      </div>
      {/* Each tile carries the chart form that suits ITS measure — a day strip
          for coverage, pips for composition, an arc for a share, a sparkline
          for a distribution. Four identical progress bars said nothing. */}
      <div style={{ position: "relative", marginTop: t.s[3], height: 34, display: "flex", alignItems: "flex-end" }}>
        {chart || (
          <div style={{ width: "100%", height: 5, borderRadius: t.r.pill, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, overflow: "hidden", position: "relative", marginBottom: 3 }}>
            <div style={{ height: "100%", width: `${w}%`, borderRadius: t.r.pill, background: color || t.c.primary, transition: `width .9s cubic-bezier(.16,1,.3,1)` }} />
            {target != null && (
              <div style={{ position: "absolute", top: -3, left: `${target}%`, width: 2, height: 11, background: t.c.textTertiary, borderRadius: 1 }} />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ── KPI micro-charts ─────────────────────────────────────────────────────
   Small, unlabelled and supplementary: the tile's value is always readable as
   text above them, so these add shape rather than carrying the reading. */

/* ── KPI micro-charts ─────────────────────────────────────────────────────
   Small and supplementary: the tile's value is always readable as text above
   them, so these carry shape, not the reading.

   Built from flex divs rather than stretched SVG. A `preserveAspectRatio:none`
   viewBox distorts rounded corners and stroke widths at these sizes, which is
   what made the first attempt look broken. Every chart below also has an
   explicit empty state — a day with no activity must render as a deliberate
   baseline, not as two disconnected stray lines. */

function EmptyRail({ note }) {
  const t = useDashTheme();
  return (
    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
      <span style={{ flex: 1, height: 2, borderRadius: 1, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover }} />
      <span style={{ fontSize: 10, color: t.c.textTertiary, whiteSpace: "nowrap" }}>{note}</span>
    </div>
  );
}

/** Coverage as a miniature of the day — same shape as the big ribbon.
 *  Gaps are left as visible track so "when they worked" reads at a glance. */
export function MiniDayStrip({ blocks = [], bounds, colors }) {
  const t = useDashTheme();
  const t0 = bounds?.startMs, t1 = bounds?.endMs;
  const span = Math.max(1, (t1 || 0) - (t0 || 0));
  const real = blocks.filter((b) => b.cat !== "idle");
  if (!real.length || !t0) return <EmptyRail note="nothing tracked" />;

  return (
    <div style={{ width: "100%", marginBottom: 3 }}>
      <div style={{
        position: "relative", height: 14, borderRadius: 4,
        background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, overflow: "hidden",
      }}>
        {real.map((b, i) => (
          <motion.i
            key={i}
            initial={{ opacity: 0, scaleY: 0.4 }} animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 0.4, ease: t.motion.ease.out, delay: 0.15 + i * 0.015 }}
            style={{
              position: "absolute", top: 2, bottom: 2, borderRadius: 2,
              left: `${((b.startMs - t0) / span) * 100}%`,
              width: `${Math.max(0.8, ((b.endMs - b.startMs) / span) * 100)}%`,
              background: colors[b.cat] || colors.browse,
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3, fontSize: 9.5, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
        <span>{fmtClock(t0)}</span><span>{fmtClock(t1)}</span>
      </div>
    </div>
  );
}

/** Composition as a small column chart — one column per thread, height by
 *  time spent. A single thread renders as one column against a baseline, which
 *  still reads as a chart; a full-width filled bar did not. */
export function MiniColumns({ values = [], color, emptyNote = "nothing to show", maxBars = 14 }) {
  const t = useDashTheme();
  const vals = values.filter((v) => v > 0).slice(0, maxBars);
  if (!vals.length) return <EmptyRail note={emptyNote} />;
  const max = Math.max(...vals);

  return (
    <div style={{ width: "100%", marginBottom: 3 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 24 }}>
        {vals.map((v, i) => (
          <motion.i
            key={i}
            initial={{ height: 2 }} animate={{ height: `${Math.max(12, (v / max) * 100)}%` }}
            transition={{ duration: 0.5, ease: t.motion.ease.out, delay: 0.18 + i * 0.05 }}
            style={{
              // Cap the width so one or two columns don't stretch into slabs.
              flex: "1 1 0", maxWidth: 26, minWidth: 5,
              borderRadius: "3px 3px 1px 1px", background: color,
              opacity: 1 - Math.min(i * 0.07, 0.4),
            }}
          />
        ))}
        <span style={{ flex: vals.length < 6 ? 6 - vals.length : 0 }} />
      </div>
      <div style={{ height: 1, background: t.c.border, marginTop: 3, borderRadius: 1 }} />
    </div>
  );
}

/** A share of a whole, as a proper semicircle gauge.
 *  Fixed aspect viewBox with default preserveAspectRatio — the earlier version
 *  stretched a 34×20 box to full width, which is why it rendered as a tiny
 *  broken arc floating in the middle of the tile. */
export function MiniGauge({ pct = 0, color, target }) {
  const t = useDashTheme();
  const p = Math.max(0, Math.min(100, pct || 0));
  const R = 40, CX = 50, CY = 48;
  const LEN = Math.PI * R;
  const path = `M${CX - R},${CY} A${R},${R} 0 0 1 ${CX + R},${CY}`;
  const ang = Math.PI - (Math.PI * (target ?? 0)) / 100;

  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 2 }}>
      <svg width="88" height="34" viewBox="0 0 100 54" aria-hidden="true" style={{ overflow: "visible" }}>
        <path d={path} fill="none" strokeLinecap="round" strokeWidth="9"
              stroke={t.isDark ? t.c.surfaceMuted : t.c.surfaceHover} />
        {p > 0 && (
          <motion.path
            d={path} fill="none" strokeLinecap="round" strokeWidth="9" stroke={color}
            strokeDasharray={LEN}
            initial={{ strokeDashoffset: LEN }} animate={{ strokeDashoffset: LEN - (LEN * p) / 100 }}
            transition={{ duration: 1, ease: t.motion.ease.out, delay: 0.2 }}
          />
        )}
        {target != null && (
          <line
            x1={CX + (R - 7) * Math.cos(ang)} y1={CY - (R - 7) * Math.sin(ang)}
            x2={CX + (R + 7) * Math.cos(ang)} y2={CY - (R + 7) * Math.sin(ang)}
            stroke={t.c.textTertiary} strokeWidth="2" strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  );
}

/* ── Labelled bar row ─────────────────────────────────────────────────── */
export function BarRow({ label, dot, value, sub, fill, color, minLabel = 104, marker, index = 0 }) {
  const t = useDashTheme();
  const [w, setW] = React.useState(0);
  React.useEffect(() => {
    const id = setTimeout(() => setW(Math.max(0, Math.min(100, fill))), 200 + index * 70);
    return () => clearTimeout(id);
  }, [fill, index]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderTop: index ? `1px solid ${t.c.border}` : "none", minWidth: 0 }}>
      {/* The label may shrink and ellipsis rather than force the row wider —
          otherwise a long label pushes the value out of a narrow card. */}
      <div style={{ fontSize: t.font.size.base, flex: `0 1 ${minLabel}px`, minWidth: 62, display: "flex", alignItems: "center", gap: 7 }}>
        {dot && <i style={{ width: 8, height: 8, borderRadius: 2, background: dot, flex: "none" }} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={typeof label === "string" ? label : undefined}>{label}</span>
      </div>
      <div style={{ flex: "1 1 40px", minWidth: 34, height: 8, borderRadius: t.r.pill, background: t.isDark ? t.c.surfaceMuted : t.c.surfaceHover, overflow: "hidden", position: "relative" }}>
        <div style={{ height: "100%", width: `${w}%`, borderRadius: t.r.pill, background: color, transition: "width .8s cubic-bezier(.16,1,.3,1)" }} />
        {marker != null && (
          <div style={{ position: "absolute", top: -3, left: `${marker}%`, width: 2, height: 14, background: t.c.textTertiary, borderRadius: 1 }} />
        )}
      </div>
      {/* The value must never be the thing that gets clipped — it is the
          reading. flex:none keeps it whole and lets the bar absorb the squeeze. */}
      <div style={{ fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, flex: "none", minWidth: 52, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{value}</div>
      {sub != null && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, flex: "none", minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{sub}</div>}
    </div>
  );
}

/* ── Activity meter — 8 segments, a level not a count ─────────────────── */
export function ActivityMeter({ value = 0, label, delay = 0 }) {
  const t = useDashTheme();
  const [lit, setLit] = React.useState(0);
  const target = Math.round((value / 100) * 8);
  React.useEffect(() => {
    let i = 0;
    const step = () => { i += 1; setLit(i); if (i < target) setTimeout(step, 38); };
    const id = setTimeout(step, delay);
    return () => clearTimeout(id);
  }, [target, delay]);
  const tone = activityTone(t, value);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, textTransform: "uppercase", letterSpacing: "0.06em", color: t.c.textTertiary }}>
        Activity
      </span>
      <span style={{ flex: 1, display: "flex", gap: 3 }} aria-hidden="true">
        {Array.from({ length: 8 }).map((_, i) => (
          <i key={i} style={{ flex: 1, height: 6, borderRadius: 2, background: i < lit ? tone : t.c.surfaceMuted, transition: "background .4s cubic-bezier(.16,1,.3,1)" }} />
        ))}
      </span>
      <span style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, color: tone }}>{label}</span>
    </div>
  );
}

/* ── Chip ─────────────────────────────────────────────────────────────── */
export function Chip({ children, tone, soft, style }) {
  const t = useDashTheme();
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: t.r.pill,
      background: soft || t.c.surfaceMuted, border: `1px solid ${tone ? `${tone}55` : t.c.border}`,
      fontSize: t.font.size.xs, color: tone || t.c.textSecondary, whiteSpace: "nowrap", ...style,
    }}>{children}</span>
  );
}

/* ── Section label ────────────────────────────────────────────────────── */
export function SectionLabel({ children, style }) {
  const t = useDashTheme();
  return (
    <div style={{ fontSize: 10.5, fontWeight: t.font.weight.semibold, textTransform: "uppercase", letterSpacing: "0.07em", color: t.c.textTertiary, marginBottom: 7, ...style }}>
      {children}
    </div>
  );
}

/* ── Source toggle ────────────────────────────────────────────────────── */
export function SourceToggle({ value, onChange, coverage }) {
  const t = useDashTheme();
  const opts = [
    { key: "app", label: "App", color: t.c.primary, has: coverage?.app },
    { key: "agent", label: "Agent", color: t.c.ai, has: coverage?.agent },
    { key: "both", label: "Both", color: null, has: true },
  ];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, textTransform: "uppercase", letterSpacing: "0.08em", color: t.c.textTertiary }}>Source</span>
      <div style={{ display: "flex", background: t.c.surfaceMuted, padding: 3, borderRadius: t.r.md, border: `1px solid ${t.c.border}` }} role="group" aria-label="Data source">
        {opts.map((o) => {
          const on = value === o.key;
          return (
            <button
              key={o.key} type="button" onClick={() => onChange(o.key)} aria-pressed={on}
              title={o.has === false ? `No ${o.label.toLowerCase()} data for this day` : undefined}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: t.r.sm,
                fontSize: t.font.size.md, fontWeight: t.font.weight.medium, cursor: "pointer", border: "none",
                background: on ? t.c.surface : "transparent",
                color: on ? t.c.text : (o.has === false ? t.c.textTertiary : t.c.textSecondary),
                boxShadow: on ? t.shadow.sm : "none",
                transition: "all .18s cubic-bezier(.16,1,.3,1)",
              }}
            >
              <i style={{
                width: 8, height: 8, borderRadius: 2,
                background: o.color || `linear-gradient(135deg, ${t.c.primary} 0 50%, ${t.c.ai} 50% 100%)`,
                opacity: o.has === false ? 0.35 : 1,
              }} />
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
